import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service, UserListOption } from 'webapp-types';

import ClientNewRowComponent from 'webapp/components/client/new-row';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type Space from 'webapp/models-custom/space';

interface Args {
	model: Space;
	usersForAccountManager: UserListOption[];
	onInvite: (forClient?: Space) => void;
	onContentChanged: () => void;
	onClose: () => void;
}

export default class ClientNewComponent extends Component<Args> {
	@service('store') declare storeService: Service.Store;
	@service('infolayer') declare infoLayerService: Service.Infolayer;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;

	@tracked submitInProgress = false;
	@tracked clients: Space[] = [];

	maxRows = 10; // Max number of clients

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const maxClients = this.billingService.features.clientsMax;
		if (maxClients !== null) {
			const clientsLeft = maxClients - this.billingService.clientsCurrent;
			this.maxRows = Math.min(clientsLeft, 10);
		}

		next(this, this.setEmptyLines);
	}

	get canAddRow() {
		return this.clients.length < this.maxRows;
	}

	get canDeleteItem() {
		return this.clients.length > 1 && !this.submitInProgress;
	}

	get isAddSectionShown() {
		return this.clients.length < this.maxRows;
	}

	addEmptySpace() {
		if (this.canAddRow) {
			const spaceObj = this.mmSpacesService.createNewClient();

			this.clients = [...this.clients, spaceObj];
		}
	}

	setEmptyLines() {
		// if user has only 10 or less clients added, the initial number of rows is 3.
		let linesToAdd = 1;

		if (this.mmSpacesService.otherSpaceRecords.length < 11) {
			linesToAdd = 3;
		}

		for (let i = 0; i < linesToAdd; i++) {
			this.addEmptySpace();
		}

		schedule('afterRender', this, this.args.onContentChanged);
	}

	@action
	create() {
		const validateFields = ['companyName', 'websiteUrl'],
			clients = this.clients.filter((client) => client.companyName);

		// If all clients name is empty validate only first client to inform user
		if (!clients.length) {
			this.clients[0]!.validate(validateFields);
			return;
		}

		// Validate all clients (without empty ones) that has companyName
		let errors = false;
		clients.forEach((client) => {
			const success = client.validate(validateFields);

			if (!success) {
				errors = true;
			}
		});

		if (errors) {
			return;
		}

		this.submitInProgress = true;
		void this.mmSpacesService.saveClients(clients).then(() => {
			this.submitInProgress = false;
			this.close();
		});
	}

	@action
	addNewRow() {
		this.addEmptySpace();
		this.args.onContentChanged();
	}

	@action
	deleteItem(item: Space) {
		if (this.canDeleteItem) {
			this.clients = this.clients.filter((c) => c !== item);

			void item.delete();

			this.args.onContentChanged();
		}
	}

	@action
	close() {
		if (!this.submitInProgress) {
			this.args.onClose();
		}
	}
	<template>
		<div class="row row--no-margin">
			<div class="col">
				<label>Account name</label>
			</div>
			<div class="col">
				<label>Website</label>
			</div>
			<div class="col">
				<label>Account manager</label>
			</div>
		</div>

		{{#each this.clients as |client|}}
			<ClientNewRowComponent
				@model={{client}}
				@isDeleteBtnShown={{this.canDeleteItem}}
				@usersForAccountManager={{@usersForAccountManager}}
				@submitInProgress={{this.submitInProgress}}
				@onInvite={{@onInvite}}
				@onDelete={{this.deleteItem}}
			/>
		{{/each}}

		{{#if this.isAddSectionShown}}
			<div class="row">
				<div class="add {{unless this.canAddRow 'add--no-space'}}">
					<a role="button" class="label" {{on "click" this.addNewRow}}>
						+ Add another account
					</a>
				</div>
			</div>
		{{/if}}

		<div class="controls2">
			<div class="controls2__right">
				<SubmitButtonComponent
					@text="Cancel"
					@theme="grey-empty"
					@noDelay={{true}}
					@onTrigger={{this.close}}
				/>
				<SubmitButtonComponent
					@text="Create"
					@extraClass="create"
					@onTrigger={{this.create}}
					@submitInProgress={{this.submitInProgress}}
				/>
			</div>
		</div>
	</template>
}
